import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode , type CSSProperties } from 'react';

import { prefersReducedMotion } from '../../lib/motion';

/**
 * حبر الالتقاط (§5.9-1): عند الحفظ تُمتص نقطة كهرمانية من زر الإرسال نحو أيقونة
 * الصندوق في التنقل خلال dur3. الرسالة الوحيدة: كلماتك وصلت مكانًا آمنًا.
 *
 * الطبقة تعيش فوق سطح الالتقاط لا داخله — لوحة الالتقاط تُغلق فورًا عند الحفظ،
 * والنقطة يجب أن تُكمل رحلتها بعد إغلاقها.
 *
 * `prefers-reduced-motion` يلغيها كليًا (§5.6-5)، وغياب أيقونة الصندوق (شاشة تسجيل
 * دخول مثلًا) يلغيها بهدوء — البهجة لا تكون شرطًا لنجاح الحفظ أبدًا.
 */

interface Point {
  readonly x: number;
  readonly y: number;
}

interface Trail {
  readonly key: number;
  readonly from: Point;
  readonly to: Point;
}

interface InkTrailValue {
  readonly fly: (origin: DOMRect | null) => void;
}

const InkTrailContext = createContext<InkTrailValue | null>(null);

export function useInkTrail(): InkTrailValue {
  return useContext(InkTrailContext) ?? { fly: () => undefined };
}

const DOT_SIZE = 8;

function centerOf(rect: DOMRect): Point {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

/**
 * هدف الرحلة: رابط الصندوق الظاهر فعلًا — الشريط الجانبي على الديسكتوب والشريط
 * السفلي على الجوال، وأحدهما مخفي بـ display:none فلا مستطيل له.
 */
function inboxTarget(): Point | null {
  for (const element of document.querySelectorAll('a[href="/inbox"]')) {
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return centerOf(rect);
  }
  return null;
}

export function InkTrailProvider({ children }: { children: ReactNode }) {
  const [trails, setTrails] = useState<readonly Trail[]>([]);
  const counter = useRef(0);

  const fly = useCallback((origin: DOMRect | null) => {
    if (prefersReducedMotion() || origin === null) return;
    const to = inboxTarget();
    if (to === null) return;
    counter.current += 1;
    setTrails((previous) => [...previous, { key: counter.current, from: centerOf(origin), to }]);
  }, []);

  const value = useMemo<InkTrailValue>(() => ({ fly }), [fly]);

  return (
    <InkTrailContext.Provider value={value}>
      {children}
      {/* dir=ltr مقصود: الطبقة هندسة محضة بإحداثيات viewport، فتصير start = left
          وتتوافق قيم x مع getBoundingClientRect بلا انعكاس. لا نص فيها إطلاقًا. */}
      <div dir="ltr" aria-hidden="true" className="pointer-events-none fixed inset-0 z-[var(--z-toast)] overflow-hidden">
        {trails.map((trail) => (
          <span
            key={trail.key}
            className="animate-ink-trail absolute top-0 start-0 block rounded-full bg-accent"
            style={
              {
                width: DOT_SIZE,
                height: DOT_SIZE,
                '--ink-from-x': `${trail.from.x - DOT_SIZE / 2}px`,
                '--ink-from-y': `${trail.from.y - DOT_SIZE / 2}px`,
                '--ink-to-x': `${trail.to.x - DOT_SIZE / 2}px`,
                '--ink-to-y': `${trail.to.y - DOT_SIZE / 2}px`,
              } as CSSProperties
            }
            onAnimationEnd={() =>
              setTrails((previous) => previous.filter((item) => item.key !== trail.key))
            }
          />
        ))}
      </div>
    </InkTrailContext.Provider>
  );
}
